/* eslint-disable react/prop-types */
import "../styles/globals.scss";
import "../styles/fonts.scss";
import Layout from "@/components/layout/index";
import { CssBaseline, GlobalStyles, ThemeProvider } from "@mui/material";
import {lightTheme, darkTheme} from "../styles/themes";
import "tailwindcss/tailwind.css";
import React, { useEffect } from "react";
import { useRouter } from "next/router";

import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "../store/store";
import { Provider } from "react-redux";
import GlobalLoader from "@/components/atoms/loader/globalLoader";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
const queryClient = new QueryClient();
import { GoogleOAuthProvider } from "@react-oauth/google";

import * as gtag from "../lib/gtag";
import Script from "next/script";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallBack from "@/components/atoms/errorBoundary";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setPreviewOpen } from "@/store/assistance";
import { isIframe } from "@/utils/helper";
import { setTask, taskUpdated, toggleModal } from "@/store/task";
import { toggleTaskEdit } from "@/store/common";
import useDarkMode from "@/hooks/useDarkMode";
import AssistanceProvider from "@/context/assistanceProvider";

export default function App({ Component, pageProps }) {
  const {isDarkMode} = useDarkMode();
  return (
    <ErrorBoundary FallbackComponent={ErrorFallBack}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
            <CssBaseline />
            <GlobalStyles
              styles={{
                "*": {
                  fontFamily: "Inter, sans-serif",
                },
                body: {
                  margin: 0,
                  padding: 0,
                  boxSizing: "border-box",
                },
              }}
            />
            <GoogleOAuthProvider
              clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID}
            >
              <QueryClientProvider client={queryClient}>
                <div className="fs-24">
                  <ReactQueryDevtools />
                </div>

                <AssistanceProvider>
                  <Layout>
                    <AppContent Component={Component} pageProps={pageProps} />
                  </Layout>
                </AssistanceProvider>
              </QueryClientProvider>
            </GoogleOAuthProvider>
          </ThemeProvider>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  );
}

function AppContent({ Component, pageProps }) {
  const router = useRouter();
  const isPreviewOpen = useSelector((state) => state.assistant.preview.open);

  const dispatch = useDispatch();

  const hideWidgetOn = [
    "/organization",
    "/payment-confirmation",
    "/subscription",
    "/on-board",
    "/shared-asset",
    "/invitation/[id]",
    "/invitation/register/[email]",
    "/password-reset",
    "/ai-copilot",
    "/brand-management/[name]",
    "/account-settings/[name]",
    "/ai-mode",
    "/plans"
  ];

  useEffect(() => {
    if (hideWidgetOn.includes(router.pathname) || isPreviewOpen) {
      window.FreshworksWidget?.("hide");
    } else {
      window.FreshworksWidget?.("show");
    }
  }, [router.pathname, isPreviewOpen]);

  // Track page views on route change
  useEffect(() => {
    const handleRouteChange = (url) => {
      gtag.pageview(url);
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    if (isIframe()) {
      const isLocalPreviewOpen = localStorage.getItem("isPreviewOpen");
      dispatch(setPreviewOpen(isLocalPreviewOpen ? true : false));
    }
  }, []);

  useEffect(() => {
    const handler = (event) => {
      if (isIframe()) {
        if (event.data.type == "TASK_UPDATED") {
          dispatch(taskUpdated());
        }
      } else {
        if (event?.data?.type == "PREVIEW_TASK_MODAL") {
          if (!event?.data?.task) return;

          // opening task
          dispatch(setTask(event.data.task));
          dispatch(toggleModal());
          dispatch(toggleTaskEdit(false));
        }


      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  return (
    <>
      {/* Google Analytics Script */}
      <Script
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=G-ZRDYWHMVZV`}
      />
      <Script
        id="gtag-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-ZRDYWHMVZV', {
              page_path: window.location.pathname,
            });
          `,
        }}
      />

      <Script
        src="https://ind-widget.freshworks.com/widgets/1120000000280.js"
        strategy="afterInteractive"
        async
        defer
      />
      <Script id="freshworks-widget" strategy="afterInteractive">
        {`
          window.fwSettings = {
            'widget_id': 1120000000280
          };
          !function(){
            if("function" != typeof window.FreshworksWidget){
              var n = function(){ n.q.push(arguments) };
              n.q = [];
              window.FreshworksWidget = n;
            }
          }();
        `}
      </Script>

      <Component {...pageProps} />
      <Toaster
        position="top-right"
        gutter={12}
        toastOptions={{
          success: {
            duration: 3000,
          },
          error: {
            duration: 5000,
          },
          style: {
            fontSize: "1.4rem",
            maxWidth: "500px",
            padding: "1rem",
            color: "var(--text-300)",
          },
        }}
      />
      <GlobalLoader />
    </>
  );
}
